import React, { useRef, useState } from 'react';
import { Copy, CheckCircle, Download } from 'lucide-react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { btnClass } from './ui';

// "https://link.asakreasi.com/Aset" -> "qr-Aset.png"
// String-based on purpose: never throws if the API response shape changes.
export const qrFilename = (url, ext) => {
    const code = String(url).split('?')[0].split('#')[0].replace(/\/+$/, '').split('/').pop();
    return `qr-${code.replace(/[^a-zA-Z0-9_-]/g, '') || 'code'}.${ext}`;
};

const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    // Revoke on the next macrotask: click() has already queued the download.
    setTimeout(() => URL.revokeObjectURL(url), 0);
};

// Undefined on http:// and in browsers without async clipboard image support.
const canCopyImage =
    typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard?.write === 'function';

// Renders one short link with its QR code and the download/copy actions.
// Mount one at a time: each carries a hidden 512px canvas, and a canvas that
// size costs ~1MB of backing store (4MB on a retina screen).
export default function LinkCard({ shortUrl, onError }) {
    const [isCopied, setIsCopied] = useState(false);
    const [isImgCopied, setIsImgCopied] = useState(false);
    const svgRef = useRef(null);
    const canvasRef = useRef(null);

    const fail = (message) => (onError ? onError(message) : console.error(message));

    const handleCopy = () => {
        if (!shortUrl) return;
        navigator.clipboard.writeText(shortUrl).then(() => {
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }).catch((err) => {
            console.error('Failed to copy text: ', err);
            fail('Failed to copy URL.');
        });
    };

    const handleDownloadPng = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.toBlob((blob) => {
            if (blob) saveBlob(blob, qrFilename(shortUrl, 'png'));
        }, 'image/png');
    };

    const handleDownloadSvg = () => {
        const svg = svgRef.current;
        if (!svg) return;
        const source = new XMLSerializer().serializeToString(svg);
        saveBlob(new Blob([source], { type: 'image/svg+xml;charset=utf-8' }), qrFilename(shortUrl, 'svg'));
    };

    const handleCopyImage = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        // Safari: clipboard.write() must be reached synchronously inside the click
        // handler. toBlob is async, so hand ClipboardItem the promise, not the blob.
        const png = new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        navigator.clipboard
            .write([new ClipboardItem({ 'image/png': png })])
            .then(() => {
                setIsImgCopied(true);
                setTimeout(() => setIsImgCopied(false), 2000);
            })
            .catch((err) => {
                console.error('Failed to copy QR image: ', err);
                fail('Failed to copy QR image.');
            });
    };

    return (
        <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 p-4 rounded-lg flex flex-col sm:flex-row items-center gap-4 animate-fade-in">
            {/* Colors are hardcoded, never themed: the QR must stay dark-on-light. */}
            <div className="bg-white p-2 rounded-md shrink-0">
                <QRCodeSVG
                    ref={svgRef}
                    value={shortUrl}
                    size={160}
                    level="M"
                    marginSize={4}
                    bgColor="#FFFFFF"
                    fgColor="#000000"
                    title={`QR code for ${shortUrl}`}
                />
            </div>

            {/* Off-screen twin, only used for PNG export + clipboard. */}
            <QRCodeCanvas
                ref={canvasRef}
                value={shortUrl}
                size={512}
                level="M"
                marginSize={4}
                bgColor="#FFFFFF"
                fgColor="#000000"
                className="hidden"
                aria-hidden="true"
            />

            <div className="flex-1 min-w-0 w-full flex flex-col gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <CheckCircle className="text-green-500 shrink-0" size={24} />
                    <a href={shortUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-green-400 font-medium hover:underline break-all">
                        {shortUrl}
                    </a>
                </div>

                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                    <button onClick={handleCopy} className={btnClass(isCopied)}>
                        <Copy size={16} className="mr-2" />
                        {isCopied ? 'Copied!' : 'Copy'}
                    </button>
                    <button onClick={handleDownloadPng} className={btnClass(false)}>
                        <Download size={16} className="mr-2" />PNG
                    </button>
                    <button onClick={handleDownloadSvg} className={btnClass(false)}>
                        <Download size={16} className="mr-2" />SVG
                    </button>
                    {canCopyImage && (
                        <button onClick={handleCopyImage} className={btnClass(isImgCopied)}>
                            <Copy size={16} className="mr-2" />
                            {isImgCopied ? 'Copied!' : 'Copy QR'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
